import React from "react";
import { Row, Col, Card } from "react-bootstrap";
import DefaultButton from "../../../../shared/components/DefaultButton";
import "./EmailConditionBuilder.css";
import { useFormikContext, ErrorMessage } from "formik";


const EmailConditionBuilder = ({ values, setFieldValue }) => {
  const { errors, touched } = useFormikContext();

  const emailFields = [
    { value: "body", label: "Body", dataType: "STRING" },
    { value: "sender_email", label: "Sender Email", dataType: "STRING" },
    { value: "subject", label: "Subject", dataType: "STRING" },
    { value: "subject_or_body", label: "Subject or Body", dataType: "STRING" },
    { value: "to", label: "To", dataType: "STRING" },
  ];

  const operators = [
    { value: "equals", label: "Equals" },
    { value: "not_equals", label: "Not Equals" },
    { value: "is_null", label: "Is Null" },
    { value: "is_not_null", label: "Is Not Null" },
    { value: "in", label: "Contains" },
    { value: "not_in", label: "Not Contains" },
  ];

  const groupColorClasses = [
    { summary: "group-blue", container: "condition-group-blue", badge: "group-badge-blue" },
    { summary: "group-pink", container: "condition-group-pink", badge: "group-badge-pink" },
    { summary: "group-green", container: "condition-group-green", badge: "group-badge-green" },
    { summary: "group-orange", container: "condition-group-orange", badge: "group-badge-orange" },
  ];

  const addGroup = () => {
    const currentGroups = values.conditionGroups || [];
    const newGroup = {
      internalOperator: "AND",
      nextOperator: null,
      conditions: [
        {
          field_code: "body",
          field_name: "Body",
          operator: "equals",
          value: "",
          valueLabel: "",
          dataType: "STRING",
        },
      ],
    };

    // Update nextOperator of the last group if it exists
    if (currentGroups.length > 0) {
      currentGroups[currentGroups.length - 1].nextOperator = "AND";
    }

    setFieldValue("conditionGroups", [...currentGroups, newGroup]);
  };

  const addConditionToGroup = (groupIndex) => {
    const newCondition = {
      field_code: "body",
      field_name: "Body",
      operator: "equals",
      value: "",
      valueLabel: "",
      dataType: "STRING",
    };
    const updatedGroups = [...values.conditionGroups];
    updatedGroups[groupIndex].conditions.push(newCondition);
    setFieldValue("conditionGroups", updatedGroups);
  };

  const removeGroup = (groupIndex) => {
    const updatedGroups = values.conditionGroups.filter(
      (_, index) => index !== groupIndex
    );
    
    // Update nextOperator for the new last group
    if (updatedGroups.length > 0) {
      updatedGroups[updatedGroups.length - 1].nextOperator = null;
    }
    
    setFieldValue("conditionGroups", updatedGroups);
  };

  const removeCondition = (groupIndex, conditionIndex) => {
    const updatedGroups = [...values.conditionGroups];
    updatedGroups[groupIndex].conditions = updatedGroups[
      groupIndex
    ].conditions.filter((_, index) => index !== conditionIndex);
    setFieldValue("conditionGroups", updatedGroups);
  };

  const updateCondition = (groupIndex, conditionIndex, field, value) => {
    const updatedGroups = [...values.conditionGroups];
    
    if (field === "field_code") {
      const selectedField = emailFields.find((f) => f.value === value);
      updatedGroups[groupIndex].conditions[conditionIndex].field_code = value;
      updatedGroups[groupIndex].conditions[conditionIndex].field_name = selectedField?.label || value;
      updatedGroups[groupIndex].conditions[conditionIndex].dataType = selectedField?.dataType || "STRING";
    } else if (field === "operator") {
      updatedGroups[groupIndex].conditions[conditionIndex].operator = value;
    } else if (field === "value") {
      updatedGroups[groupIndex].conditions[conditionIndex].value = value;
      updatedGroups[groupIndex].conditions[conditionIndex].valueLabel = value;
    }
    
    setFieldValue("conditionGroups", updatedGroups);
  };

  const updateGroupLogic = (groupIndex, logic) => {
    const updatedGroups = [...values.conditionGroups];
    updatedGroups[groupIndex].internalOperator = logic;
    setFieldValue("conditionGroups", updatedGroups);
  };

  const getActiveConditionsSummary = () => {
    if (!values.conditionGroups || values.conditionGroups.length === 0) {
      return [];
    }
    
    return values.conditionGroups.map((group, index) => {
      const groupLabel = `Group ${index + 1}: ${group.conditions
        .map((c) => {
          const operatorLabel =
          operators.find((op) => op.value === c.operator)?.label || c.operator;

          return `${c.field_name} ${operatorLabel}`;

        })
        .join(` ${group.internalOperator} `)}`;
      return { index, label: groupLabel, logic: group.internalOperator };
    });
  };

  const activeSummary = getActiveConditionsSummary();

  return (
    <Card className="mb-4">
      <Card.Header className="d-flex justify-content-between align-items-center">
        <span>Text to Search For</span>
        <div className="d-flex gap-2">
          <button type="button" className="btn condition-builder-btn">
            Condition Builder
          </button>
          <button type="button" className="btn add-group-btn" onClick={addGroup}>
            + Add Group
          </button>
        </div>
      </Card.Header>
      <Card.Body>
        <p className="text-muted mb-3 condition-description">
          Add dynamic filters with AND/OR logic between condition groups
        </p>

        {/* Active Conditions Summary */}
        {activeSummary.length > 0 && (
          <div className="mb-4">
            <strong className="active-conditions-label">Active Conditions:</strong>
            <div className="d-flex flex-wrap gap-2 align-items-center mt-2">
              {activeSummary.map((item, idx) => (
                <React.Fragment key={item.index}>
                  <span
                    className={`group-summary-badge ${
                      groupColorClasses[item.index % groupColorClasses.length].summary
                    }`}
                  >
                    {item.label}
                  </span>
                  {idx < activeSummary.length - 1 && (
                    <span className="and-separator-badge">
                      {values.conditionGroups[idx].nextOperator || "AND"}
                    </span>
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>
        )}

        {/* Condition Groups */}
        {values.conditionGroups && values.conditionGroups.map((group, groupIndex) => (
          <React.Fragment key={groupIndex}>
            <div
              className={`condition-group-container ${
                groupColorClasses[groupIndex % groupColorClasses.length].container
              }`}
            >
              <div className="d-flex justify-content-between align-items-center mb-3">
                <div className="d-flex align-items-center gap-2">
                    <span
                    className={`group-badge ${
                        groupColorClasses[groupIndex % groupColorClasses.length].badge
                    }`}
                    >
                    Group {groupIndex + 1}
                    </span>
                    <select
                    className="form-select logic-dropdown"
                    value={group.internalOperator}
                    onChange={(e) =>
                        updateGroupLogic(groupIndex, e.target.value)
                    }
                    >
                    <option value="AND">AND</option>
                    <option value="OR">OR</option>
                    </select>
                    <span className="text-muted logic-label">
                    All conditions must match
                    </span>
                </div>
                <div className="d-flex align-items-center gap-2">
                    <button
                    type="button"
                    className="btn add-condition-btn"
                    onClick={() => addConditionToGroup(groupIndex)}
                    >
                    + Add Condition
                    </button>
                    <i
                    className="fas fa-trash"
                    style={{
                        color: "#d62728",
                        cursor: "pointer",
                        fontSize: "1rem",
                    }}
                    onClick={() => removeGroup(groupIndex)}
                    title="Delete group"
                    />
                </div>
                </div>

              {/* Conditions in Group */}
                {group.conditions.map((condition, conditionIndex) => (
                <Row
                    key={conditionIndex}
                    className="g-2 mb-2 align-items-center"
                >
                    <Col md="auto">
                    <span className="condition-number-badge">
                        {conditionIndex + 1}
                    </span>
                    </Col>
                    <Col md={3}>
                    <select
                        className="form-select condition-select"
                        value={condition.field_code}
                        onChange={(e) =>
                        updateCondition(
                            groupIndex,
                            conditionIndex,
                            "field_code",
                            e.target.value
                        )
                        }
                    >
                        {emailFields.map((field) => (
                        <option key={field.value} value={field.value}>
                            {field.label}
                        </option>
                        ))}
                    </select>
                    <ErrorMessage
                      name={`conditionGroups.${groupIndex}.conditions.${conditionIndex}.field_code`}
                      component="div"
                      className="error-text"
                    />

                    </Col>
                    <Col md={2}>
                    <select
                        className="form-select condition-select"
                        value={condition.operator}
                        onChange={(e) =>
                        updateCondition(
                            groupIndex,
                            conditionIndex,
                            "operator",
                            e.target.value
                        )
                        }
                    >
                        {operators.map((op) => (
                        <option key={op.value} value={op.value}>
                            {op.label}
                        </option>
                        ))}
                    </select>
                    <ErrorMessage
                      name={`conditionGroups.${groupIndex}.conditions.${conditionIndex}.operator`}
                      component="div"
                      className="error-text"
                    />

                    </Col>
                    <Col md={6}>
                    <input
                        type="text"
                        className="form-control condition-input"
                        placeholder="Enter value..."
                        value={condition.value}
                        onChange={(e) =>
                        updateCondition(
                            groupIndex,
                            conditionIndex,
                            "value",
                            e.target.value
                        )
                        }
                    />
                    <ErrorMessage
                      name={`conditionGroups.${groupIndex}.conditions.${conditionIndex}.value`}
                      component="div"
                      className="error-text"
                    />

                    </Col>
                    <Col md="auto" className="ms-auto">
                    <i
                        className="fas fa-trash"
                        style={{
                        color: "#d62728",
                        cursor: "pointer",
                        fontSize: "1rem",
                        }}
                        onClick={() =>
                        removeCondition(groupIndex, conditionIndex)
                        }
                        title="Delete condition"
                    />
                    </Col>
                </Row>
                ))}
            </div>

            {/* AND/OR separator dropdown between groups */}
            {groupIndex < values.conditionGroups.length - 1 && (
              <div className="group-separator">
                <select
                  className="form-select group-separator-dropdown"
                  value={group.nextOperator || "AND"}
                  onChange={(e) => {
                    const updatedGroups = [...values.conditionGroups];
                    updatedGroups[groupIndex].nextOperator = e.target.value;
                    setFieldValue("conditionGroups", updatedGroups);
                  }}
                >
                  <option value="AND">AND</option>
                  <option value="OR">OR</option>
                </select>
              </div>
            )}
          </React.Fragment>
        ))}
      </Card.Body>
    </Card>
  );
};

export default EmailConditionBuilder;